import * as Mui from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { useForm } from "react-hook-form";
import DamageFormService from "../../service/DamageFormService";
import useSnackbar from "../../../../hooks/useSnackbar";
import { useParams, useSearchParams } from "react-router-dom";
import { DamageFormTypes } from "../../Types";
import FormInput from "../../../common/FormInput";
import { nameof } from "../../../common/ObjectUtils";
import { useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ContextOperation } from "../../../../context/OperationContext";
import { ContextDamageForm } from "../../DamageFormContext";
import { AutoCompleteOption } from "../../../common/AutoComplete";
import useOperationTranslation from "../../../../hooks/useOperationTranslation";
import useDamageFormActions from "../../service/DamageFormActions";
import { useTranslation } from "react-i18next";
import { useHasScope } from "../../../../../../config/globalConfig";
import {
  AMOUNT_PLACEHOLDERS,
  PLACEHOLDER_META,
  inferTemplatePlaceholders,
  renderTemplate,
  validateEditablePlaceholder,
} from "./placeholders";

interface Props {
  open: boolean;
  onClose: () => void;
}

type GeneralInfoVehicleFallback = DamageFormTypes.IGeneralInfo & {
  vehicle?: string;
  vehicleModel?: string;
  model?: string;
};

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <Mui.Typography
    variant="body2"
    sx={{ display: "block", mb: 0.25, fontWeight: 600, color: "text.secondary" }}
  >
    {children}
  </Mui.Typography>
);

// Mappa templateParam -> chiave i18n per le label dei placeholder editabili mostrati in modale.
const PLACEHOLDER_LABEL_KEY: Record<string, string> = {
  TemplateRequestedAmount: "damageForm.details.messages.dialogs.newMessage.placeholders.requestedAmount",
  TemplateReportType: "damageForm.details.messages.dialogs.newMessage.placeholders.reportType",
  TemplateOperatorMail: "damageForm.details.messages.dialogs.newMessage.placeholders.operatorMail",
  TemplateSource: "damageForm.details.messages.dialogs.newMessage.placeholders.source",
  TemplateDest: "damageForm.details.messages.dialogs.newMessage.placeholders.dest",
};

const MISSING_REQUIRED_BUSINESS_PLACEHOLDER_PATTERN =
  /Missing required business placeholder\s*\{\{\s*(\w+)\s*\}\}/i;

// LPP-24664 (P3): split di una lista destinatari "a, b, c," in indirizzi normalizzati.
const splitRecipients = (value?: string): string[] =>
  (value || "")
    .split(",")
    .map((address) => address.trim())
    .filter((address) => address.length > 0);

const CUSTOM_MESSAGE_BODY_PLACEHOLDER = "TemplateMessageBody";
const CUSTOM_MESSAGE_BODY_TOKEN = `{{${CUSTOM_MESSAGE_BODY_PLACEHOLDER}}}`;
const TECHNICAL_PLACEHOLDERS = new Set([CUSTOM_MESSAGE_BODY_PLACEHOLDER]);

const stripTechnicalPlaceholders = (value?: string): string => {
  return (value || "").replaceAll(CUSTOM_MESSAGE_BODY_TOKEN, "");
};

const sanitizeFreeEditBody = (body?: string): string => {
  return stripTechnicalPlaceholders(body);
};

const getSavedFreeEditValue = (savedValue?: string, fallbackValue?: string): string => {
  return savedValue && savedValue.trim() !== "" ? savedValue : fallbackValue || "";
};

const extractSendMailErrorMessage = (error: any): string | undefined => {
  const data = error?.response?.data;
  if (!data) return undefined;

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return parsed?.msg || parsed?.errorMessage || data;
    } catch {
      return data;
    }
  }

  if (typeof data.msg === "string") {
    return data.msg;
  }

  if (typeof data.errorMessage === "string") {
    try {
      const parsed = JSON.parse(data.errorMessage);
      return parsed?.msg || parsed?.errorMessage || data.errorMessage;
    } catch {
      return data.errorMessage;
    }
  }

  return undefined;
};

const NewMessages = (props: Props) => {
  const { tenant, setLoading } = useContext(ContextOperation);
  const damageFormContext = useContext(ContextDamageForm);

  const { i18n } = useTranslation();

  const t = useOperationTranslation();

  const snackBar = useSnackbar();

  const { id } = useParams();

  const form = useForm<DamageFormTypes.IMessageForm>({
    defaultValues: {
      possibleAttachments: [],
    },
  });

  const [mailTemplates, setMailTemplates] = useState<DamageFormTypes.IMailTemplate[]>();
  const [languages, setLanguages] = useState<AutoCompleteOption[]>([]);
  const [possibleAttachments, setPossibleAttachments] = useState<
    DamageFormTypes.IPossibleAttachments[]
  >([]);
  const [nhSubjectTemplate, setNhSubjectTemplate] = useState<string>("");
  const [nhBodyTemplate, setNhBodyTemplate] = useState<string>("");
  const [nhLoading, setNhLoading] = useState<boolean>(false);
  const [placeholderValues, setPlaceholderValues] = useState<Record<string, string>>({});
  const [selectedTemplateCode, setSelectedTemplateCode] = useState<string>("");
  const [nhTemplateName, setNhTemplateName] = useState<string>("");
  const [templatePlaceholders, setTemplatePlaceholders] = useState<Record<string, string>>({});
  const [placeholderTouched, setPlaceholderTouched] = useState<Record<string, boolean>>({});
  const [placeholderValidationAttempted, setPlaceholderValidationAttempted] = useState(false);
  // LPP-24664 (P1+P2): valori placeholder da preservare attraverso il cambio lingua.
  const preservedPlaceholderValuesRef = useRef<Record<string, string> | null>(null);

  // Per vedere il bottone "Dettaglio" basta lo scope VIEW: la pagina di dettaglio carica via GET protetta da VIEW lato backend.
  // Le azioni nella pagina di dettaglio (modifica/duplica/elimina) restano gated dai rispettivi scope.
  const canViewTemplate = useHasScope("SCOPE_NOTIFICATION_HUB_TEMPLATE_VIEW");
  // LPP-24664 (P3): abilita la modifica dei destinatari di default del template.
  const canEditTemplate = useHasScope("SCOPE_CREATE_DAMAGE_MAIL_TEMPLATE");

  // LPP-24664 (P3): stato del dialog "modifica destinatari di default".
  const [recipientsDialogOpen, setRecipientsDialogOpen] = useState(false);
  const [recipientsDraft, setRecipientsDraft] = useState<string>("");
  const [savingRecipients, setSavingRecipients] = useState(false);

  // task1/task2 locali: usati se il context non li ha ancora caricati (tab non ancora visitata)
  const [localTask1, setLocalTask1] = useState<DamageFormTypes.ITask1 | undefined>(damageFormContext.task1);
  const [localTask2, setLocalTask2] = useState<DamageFormTypes.ITask2 | undefined>(damageFormContext.task2);

  // Carica task1 e task2 se non presenti nel context (modale aperta prima di visitare le tab)
  useEffect(() => {
    if (!id) return;
    const needsTask1 = !damageFormContext.task1;
    const needsTask2 = !damageFormContext.task2;
    if (needsTask1) {
      DamageFormService.details.task1.get.byId(id, tenant).then(setLocalTask1).catch(() => {});
    } else {
      setLocalTask1(damageFormContext.task1);
    }
    if (needsTask2) {
      DamageFormService.details.task2.get.byId(id, tenant).then(setLocalTask2).catch(() => {});
    } else {
      setLocalTask2(damageFormContext.task2);
    }
  }, [id, tenant, damageFormContext.task1, damageFormContext.task2]);

  // Auto-popolamento placeholder dai dati della scheda (task1, task2, generalInfo)
  // Replica la logica di DomainMailTemplateService.convertTemplateWithData() lato frontend.
  // I valori vengono usati come default iniziali; quelli editable possono essere sovrascritti dall'operatore.
  const contextPlaceholderValues = useMemo<Record<string, string>>(() => {
    const t1 = localTask1;
    const t2 = localTask2;
    const gi = damageFormContext.generalInfo;
    const giVehicle = gi as GeneralInfoVehicleFallback | undefined;

    const fmt = (date?: string | null): string => {
      if (!date) return "";
      try {
        return new Date(date).toLocaleDateString("it-IT", {
          day: "2-digit", month: "2-digit", year: "numeric",
          timeZone: "Europe/Rome",
        });
      } catch {
        return date;
      }
    };

    // Luogo sinistro: indirizzo + città (come fa il backend OPR)
    const accidentPlace = [t2?.accidentAddress, t2?.accidentCity]
      .filter(Boolean).join(" ").trim();
    const generalInfoVehicleModel = giVehicle?.vehicle || giVehicle?.vehicleModel || giVehicle?.model;

    return {
      // Dati scheda
      TemplateFormId: gi?.progressive ?? "",
      TemplateFormStatus: gi?.open === undefined || gi?.open === null
        ? "N/A_FORM_STATUS"
        : gi.open ? "OPEN" : "CLOSE",

      // Dati veicolo (task1)
      TemplateVehiclePlate: t1?.plate || gi?.plate || "N/A_VEHICLE_PLATE",
      TemplateVehicleModel: t1?.vehicle || generalInfoVehicleModel || "N/A_VEHICLE_MODEL",

      // Dati noleggio (task1)
      TemplateRaNumber: t1?.raId || "-",

      // DMS gestione (task1) — solo il nome è disponibile nel frontend (oggetto {id, descr})
      // phone/address/cap/mail non sono esposti in ITask1 frontend
      // Dati sinistro (task2)
      TemplateAccDate: fmt(t2?.accidentDate) || "N/A_ACC_DATE",
      TemplateAccPlace: accidentPlace || "N/A_ACC_PLACE",
      TemplateAccProv: t2?.accidentProvince || "N/A_ACC_PROV",

      // Dati assicurativi (task2)
      TemplateInsNumber: t2?.insuranceClaimNumber || "N/A_INS_NUMBER",

      // Controparte (task2)
      TemplateCounterpart: t2?.counterparty || "N/A_COUNTERPART",
      TemplateCpPhone: t2?.counterpartyPhone || "N/A_CP_PHONE",
      TemplateCpAddress: [t2?.counterpartyAddress, t2?.counterpartyCity, t2?.counterpartyProvince]
        .filter(Boolean).join(" - ") || "N/A_CP_ADDRESS",
      TemplateCpVehicleModel: t2?.counterpartyModel || "N/A_CP_VEHICLE_MODEL",
      TemplateCpVehiclePlate: t2?.counterpartyPlate || "N/A_CP_VEHICLE_PLATE",
      TemplateCpInsCompany: t2?.counterpartInsuranceCompany || "N/A_CP_INS_COMPANY",
    };
  }, [localTask1, localTask2, damageFormContext.generalInfo]);

  const inferredPlaceholders = useMemo(
    () => inferTemplatePlaceholders(`${nhSubjectTemplate}\n${nhBodyTemplate}`),
    [nhSubjectTemplate, nhBodyTemplate]
  );

  const activePlaceholders = useMemo(() => {
    const responsePlaceholders = Object.keys(templatePlaceholders)
      .filter((templateParam) => !TECHNICAL_PLACEHOLDERS.has(templateParam))
      .map((templateParam) => ({
        eventParam: `placeholders/${templateParam}`,
        templateParam,
        paramType: "STRING",
      }));

    // OPR estrae questa mappa dal subject/body NH originale, prima del rendering.
    // L'inferenza serve solo come fallback per template senza mappa restituita.
    return (responsePlaceholders.length > 0 ? responsePlaceholders : inferredPlaceholders)
      .filter((ph) => !TECHNICAL_PLACEHOLDERS.has(ph.templateParam));
  }, [templatePlaceholders, inferredPlaceholders]);

  const editablePlaceholders = useMemo(() => {
    return activePlaceholders.filter((ph) => {
      const meta = PLACEHOLDER_META[ph.templateParam];
      return Boolean(meta?.editable);
    });
  }, [activePlaceholders]);

  const getPlaceholderLabel = (templateParam: string) => {
    const meta = PLACEHOLDER_META[templateParam] || { label: templateParam, required: false, editable: true };
    const labelKey = PLACEHOLDER_LABEL_KEY[templateParam];
    return labelKey
      ? t(labelKey as Parameters<typeof t>[0], meta.label)
      : meta.label;
  };

  const getPlaceholderValidationError = (templateParam: string, value?: string) => {
    const label = getPlaceholderLabel(templateParam);
    const validationMessage = (key: string) =>
      i18n.t(key, { ns: "operation", label });

    return validateEditablePlaceholder(templateParam, value, {
      requiredPlaceholder: validationMessage(
        "damageForm.details.messages.dialogs.newMessage.validation.requiredPlaceholder"
      ),
      invalidAmount: validationMessage(
        "damageForm.details.messages.dialogs.newMessage.validation.invalidAmount"
      ),
      invalidEmail: validationMessage(
        "damageForm.details.messages.dialogs.newMessage.validation.invalidEmail"
      ),
    });
  };

  const placeholderErrors = useMemo(() => {
    return Object.fromEntries(
      editablePlaceholders
        .map((ph) => [
          ph.templateParam,
          placeholderValidationAttempted || placeholderTouched[ph.templateParam]
            ? getPlaceholderValidationError(ph.templateParam, placeholderValues[ph.templateParam])
            : undefined,
        ])
        .filter(([, error]) => Boolean(error))
    ) as Record<string, string>;
  }, [editablePlaceholders, placeholderValues, placeholderTouched, placeholderValidationAttempted]);

  const templateLanguage = form.watch("language") || "it";

  const previewSubject = useMemo(
    () => renderTemplate(nhSubjectTemplate, placeholderValues, templateLanguage),
    [nhSubjectTemplate, placeholderValues, templateLanguage]
  );
  const previewBody = useMemo(
    () => renderTemplate(nhBodyTemplate, placeholderValues, templateLanguage),
    [nhBodyTemplate, placeholderValues, templateLanguage]
  );
  const isFreeEditTemplate = useMemo(
    () => selectedTemplateCode === "CUSTOM",
    [selectedTemplateCode]
  );

  const actions = useDamageFormActions();

  const [searchParams] = useSearchParams();
  const [msgLang, setMsgLang] = useState(searchParams.get("language"));
  const [msg, setMsg] = useState(searchParams.get("msg"));

  const convertLanguage = (lang: string) => {

    if (lang === 'it-IT')
      return "it";
    else if (lang === 'en-EN')
      return "en";
    else if (lang === 'fr-FR')
      return "fr";
    else
      return i18n.language.toLowerCase();

  }

  const onResetForm = () => {

    var templateCustom = mailTemplates?.find((temp) => (temp.code === 'CUSTOM' && temp.language === form.watch("language")));

    if (msg !== null) {
      form.setValue("language", msgLang ?? undefined);
      form.setValue("templateId", mailTemplates?.find((mt: any) => mt.code === msg)?.id);

      templateCustom = mailTemplates?.find((temp) => (temp.code === msg && temp.language === msgLang));
    }

    setPossibleAttachments(templateCustom?.possibleAttachments || []);

    const customPossibleAttachments = templateCustom?.possibleAttachments.map(
      (attach) => (attach.selected ? attach.customId : undefined)
    );

    const currentLanguage = form.watch("language");

    form.reset({
      language: templateCustom?.language || currentLanguage || convertLanguage(i18n.language),
      body: templateCustom?.body || "",
      damageFormId: id,
      subject: templateCustom?.subject || "",
      templateId: templateCustom?.id || 0,
      title: templateCustom?.title || "",
      to: templateCustom?.standardTo || "",
      code: templateCustom?.code || "",
      possibleAttachments: customPossibleAttachments || [],
    });
    setSelectedTemplateCode(templateCustom?.code || "");

    if (msg !== null) {
      form.setValue("language", msgLang ?? undefined);
      form.setValue("templateId", mailTemplates?.find((mt: any) => mt.code === msg)?.id);
    }
  };


  const onClose = () => {
    setMsgLang(null);
    setMsg(null);
    setPlaceholderTouched({});
    setPlaceholderValidationAttempted(false);
    onResetForm();
    props.onClose();
  };

  const setFirstTimeLang = () => {
    if (form.watch("language") === undefined) {
      form.setValue("language", convertLanguage(i18n.language));
    }
  };

  const sendMail = (data: DamageFormTypes.IMessageForm) => {
    if (!id) return;

    const language = form.watch("language") || "";
    setPlaceholderValidationAttempted(true);
    const invalidPlaceholder = editablePlaceholders
      .map((ph) => getPlaceholderValidationError(ph.templateParam, placeholderValues[ph.templateParam]))
      .find(Boolean);

    if (invalidPlaceholder) {
      snackBar.show("error", invalidPlaceholder);
      return;
    }

    if (!isFreeEditTemplate) {
      data.subject = previewSubject;
      data.body = previewBody;
    }

    // Invia tutti i placeholder presenti nel template NH attivo.
    // OPR decide quali valori possono sovrascrivere la mappa calcolata lato backend.
    data.placeholders = Object.fromEntries(
      activePlaceholders
        .map((ph) => [
          ph.templateParam,
          placeholderValues[ph.templateParam] ?? "",
        ])
        .map(([key, value]) => [key, String(value).trim()])
    );

    setLoading(true);
    DamageFormService.details.messages.post.sendMail(language, data).then(
      (response) => {
        snackBar.show(
          "success",
          t(
            "damageForm.details.messages.dialogs.newMessage.snackbar.sent.success"
          )
        );
        onClose();
        actions.getTask2();
        setLoading(false);
      },
      (error) => {
        const sendMailErrorMessage = extractSendMailErrorMessage(error);
        const missingPlaceholderMatch = sendMailErrorMessage?.match(
          MISSING_REQUIRED_BUSINESS_PLACEHOLDER_PATTERN
        );

        let snackbarMessage: string;
        if (missingPlaceholderMatch) {
          // Estrae il placeholder mancante dal messaggio backend e lo mostra in modo
          // leggibile (etichetta + token tecnico) senza dover ispezionare la chiamata.
          const templateParam = missingPlaceholderMatch[1];
          const label = getPlaceholderLabel(templateParam);
          const placeholder =
            label && label !== templateParam ? `${label} {{${templateParam}}}` : `{{${templateParam}}}`;
          snackbarMessage = i18n.t(
            "damageForm.details.messages.dialogs.newMessage.snackbar.sent.templateConfigurationError",
            { ns: "operation", placeholder }
          );
        } else {
          snackbarMessage = t(
            "damageForm.details.messages.dialogs.newMessage.snackbar.sent.400error"
          );
        }

        if (error.response.status == 400) {
          snackBar.show(
            "error",
            snackbarMessage
          );
        } else {
          snackBar.show(
            "error",
            t(
              "damageForm.details.messages.dialogs.newMessage.snackbar.sent.error"
            )
          );
        }

        setLoading(false);
      }
    );
  };

  const getMailTemplates = () => {
    const language = form.watch("language");

    if (!damageFormContext.generalInfo?.progressive || !language) return;

    DamageFormService.details.messages.get
      .mailTemplates(
        tenant,
        damageFormContext.generalInfo.progressive,
        language
      )
      .then((response) => {
        // Applica la risposta solo se la lingua selezionata non è cambiata nel
        // frattempo: evita che una risposta "stale" (es. lingua di default) arrivi
        // dopo e sovrascriva con una lista vuota i template della lingua corrente.
        if (form.getValues("language") === language) {
          setMailTemplates(response);
        }
      });
  };

  const getLanguages = () => {
    DamageFormService.details.messages.get
      .languages(tenant)
      .then((response) => {
        setLanguages(
          response.map((lang) => ({
            label: lang,
            value: lang,
          }))
        );

        // Se è disponibile una sola lingua, selezionala direttamente nella modale
        // così l'operatore non deve sceglierla manualmente. Il fetch dei template
        // scatta dall'effect dedicato quando la lingua cambia (nessuna chiamata extra qui).
        if (response.length === 1) {
          form.setValue("language", response[0]);
        }
      });
  };

  // Lingue: caricate una sola volta per tenant (non dipendono dalla lingua selezionata),
  // così evitiamo di rifare la chiamata a ogni cambio lingua.
  useEffect(() => {
    getLanguages();
    setFirstTimeLang();
  }, [tenant]);

  // Template: ricaricati solo quando cambia la scheda o la lingua selezionata.
  useEffect(() => {
    getMailTemplates();
  }, [damageFormContext.generalInfo?.progressive, form.watch("language")]);

  useEffect(() => {
    const pageLanguage = convertLanguage(i18n.language);
    // Allinea la lingua della modale a quella della pagina solo se è tra quelle
    // disponibili per il tenant. Se non lo è (es. tenant con un'unica lingua diversa
    // dalla lingua di pagina) manteniamo la selezione corrente, così il selettore
    // non si svuota costringendo l'operatore a reinserire la lingua.
    if (languages.length > 0 && !languages.some((l) => l.value === pageLanguage)) {
      return;
    }
    form.setValue("language", pageLanguage);
  }, [i18n.language]);

  useEffect(() => {
    if (mailTemplates && msg !== null) {
      form.setValue("language", msgLang ?? undefined);
    }

  }, []);

  // LPP-24664 (P1+P2): al cambio lingua, se era selezionato un template, riposizionarsi
  // sullo stesso template (per CODE, stabile tra lingue) nella nuova lingua, preservando i
  // placeholder gia inseriti dall'operatore. Se il template non esiste nella nuova lingua,
  // svuotare il form (comportamento onResetForm standard).
  useEffect(() => {
    if (!mailTemplates) return;

    const previousCode = selectedTemplateCode;
    const hasRealSelection = previousCode && previousCode !== "" && previousCode !== "CUSTOM";

    if (hasRealSelection && msg === null) {
      const sameCodeTemplate = mailTemplates.find((temp) => temp.code === previousCode);
      if (sameCodeTemplate && sameCodeTemplate.id === form.getValues("templateId")) {
        // Ricarica nella stessa lingua (es. dopo il salvataggio dei destinatari di default):
        // il template selezionato non cambia, non resettare il form.
        return;
      }
      if (sameCodeTemplate) {
        // Conserva i valori dei placeholder gia digitati; onChangeTemplate (triggerato dal
        // cambio di templateId) ricarica il contenuto NH nella nuova lingua e li re-applica
        // (vedi preservedPlaceholderValuesRef). L'id del template cambia tra lingue, quindi
        // la setValue fa sempre scattare l'effect dedicato.
        preservedPlaceholderValuesRef.current = { ...placeholderValues };
        form.setValue("templateId", sameCodeTemplate.id);
        return;
      }
      // Template non disponibile nella nuova lingua: svuota.
    }

    onResetForm();
  }, [mailTemplates]);


  const mailTemplateOptions = useMemo<AutoCompleteOption[]>(() => {
    return (
      mailTemplates?.map((template) => {
        return {
          label: template.title,
          value: template.id,
        };
      }) || []
    );
  }, [mailTemplates]);

  // LPP-24664 (P3): apre il dialog di modifica partendo dai SOLI destinatari di default persistiti
  // (DB). Le mail calcolate a runtime (DMS/cliente/officina) non compaiono e non sono modificabili.
  const openRecipientsDialog = () => {
    const selectedId = form.getValues("templateId");
    const selected = mailTemplates?.find((temp) => temp.id === selectedId);
    setRecipientsDraft(splitRecipients(selected?.defaultRecipients).join(", "));
    setRecipientsDialogOpen(true);
  };

  // LPP-24664 (P3): salva i destinatari di default del template (persistiti), poi ricarica i
  // template cosi' alla prossima selezione compaiono i nuovi default.
  const saveRecipients = () => {
    const selectedId = form.getValues("templateId");
    const selected = mailTemplates?.find((temp) => temp.id === selectedId);
    const code = selected?.code;
    const language = form.getValues("language") || selected?.language;

    if (!selectedId || !code || !language) {
      snackBar.show("error", t("damageForm.details.messages.dialogs.newMessage.snackbar.recipients.error"));
      return;
    }

    const recipients = splitRecipients(recipientsDraft);

    // Mail calcolate a runtime = destinatari mostrati (standardTo) meno i default DB.
    // Restano invariate e vengono riaccodate ai nuovi default nel campo "A".
    const previousDefaults = new Set(
      splitRecipients(selected?.defaultRecipients).map((address) => address.toLowerCase())
    );
    const runtimeRecipients = splitRecipients(selected?.standardTo).filter(
      (address) => !previousDefaults.has(address.toLowerCase())
    );

    setSavingRecipients(true);
    DamageFormService.details.messages.put
      .templateRecipients(selectedId, tenant, code, language, recipients)
      .then(
        () => {
          snackBar.show("success", t("damageForm.details.messages.dialogs.newMessage.snackbar.recipients.success"));
          setRecipientsDialogOpen(false);
          setSavingRecipients(false);
          // Aggiorna il campo "to": nuovi default + mail runtime invariate (senza duplicati).
          const recipientsLower = new Set(recipients.map((address) => address.toLowerCase()));
          const newTo = [
            ...recipients,
            ...runtimeRecipients.filter((address) => !recipientsLower.has(address.toLowerCase())),
          ];
          form.setValue("to", newTo.join(", "));
          // Ricarica i template per riflettere i nuovi destinatari di default.
          getMailTemplates();
        },
        () => {
          snackBar.show("error", t("damageForm.details.messages.dialogs.newMessage.snackbar.recipients.error"));
          setSavingRecipients(false);
        }
      );
  };

  const onChangeAttachments = (val: string[]) => {
    if (val) {
      const attachments = possibleAttachments.filter((attach) =>
        val.includes(attach.customId)
      );

      form.setValue("possibleAttachments", val);
      form.setValue("attachmentsSelected", attachments);
    }
  };

  const onChangeTemplate = (val?: number) => {
    if (val) {
      const template = mailTemplates?.find((temp) => temp.id === val);

      setPossibleAttachments(template?.possibleAttachments || []);
      setPlaceholderTouched({});
      setPlaceholderValidationAttempted(false);

      const thisPossibleAttachments = template?.possibleAttachments.map(
        (attach) => (attach.selected ? attach.customId : undefined)
      );
      const attachments = template?.possibleAttachments.filter(
        (attach) => attach.selected
      );

      form.reset({
        language: template?.language,
        body: "",
        damageFormId: id,
        subject: "",
        to: template?.standardTo,
        title: template?.title,
        templateId: template?.id,
        code: template?.code,
        possibleAttachments: thisPossibleAttachments,
        attachmentsSelected: attachments,
      });
      setSelectedTemplateCode(template?.code || "");

      if (template?.code && template?.language && damageFormContext.generalInfo?.progressive) {
        const freeEditTemplate = template.code === "CUSTOM";
        setNhLoading(true);
        setNhSubjectTemplate("");
        setNhBodyTemplate("");
        setNhTemplateName("");
        setTemplatePlaceholders({});
        DamageFormService.details.messages.get
          .templateContent(
            tenant,
            damageFormContext.generalInfo.progressive,
            template.language,
            template.code
          )
          .then((response) => {
            setNhSubjectTemplate(response.subject || "");
            setNhBodyTemplate(stripTechnicalPlaceholders(response.bodyText));
            setNhTemplateName(response.templateName || "");
            if (freeEditTemplate) {
              form.setValue("subject", getSavedFreeEditValue(template.subject, response.subject));
              form.setValue("body", getSavedFreeEditValue(template.body, sanitizeFreeEditBody(response.bodyText)));
            }
            const responsePlaceholders: Record<string, string> = Object.fromEntries(
              Object.entries(response.placeholders || {})
                .filter(([templateParam]) => !TECHNICAL_PLACEHOLDERS.has(templateParam))
            );
            setTemplatePlaceholders(responsePlaceholders);

            // Inizializza i placeholder con i valori dal context della scheda.
            // I valori del context sono il punto di partenza (come faceva il backend OPR
            // in convertTemplateWithData). I placeholder editable possono essere sovrascritti
            // dall'operatore; quelli non-editable vengono solo visualizzati nel preview.
            const initialValues: Record<string, string> = { ...contextPlaceholderValues, ...responsePlaceholders };
            const responsePlaceholderKeys = Object.keys(responsePlaceholders);
            const placeholdersToInitialize = responsePlaceholderKeys.length > 0
              ? responsePlaceholderKeys.map((templateParam) => ({ templateParam }))
              : inferTemplatePlaceholders(`${response.subject || ""}\n${response.bodyText || ""}`);
            placeholdersToInitialize.forEach((p) => {
              // Se il context non ha un valore per questo placeholder, inizializza a stringa vuota
              if (initialValues[p.templateParam] === undefined) {
                initialValues[p.templateParam] = "";
              }
            });
            // LPP-24664 (P1+P2): se stiamo cambiando lingua sullo stesso template, re-applica
            // i valori gia digitati dall'operatore (solo per i placeholder ancora presenti).
            const preserved = preservedPlaceholderValuesRef.current;
            if (preserved) {
              Object.keys(initialValues).forEach((key) => {
                const prevValue = preserved[key];
                if (prevValue !== undefined && prevValue !== "") {
                  initialValues[key] = prevValue;
                }
              });
              preservedPlaceholderValuesRef.current = null;
            }
            setPlaceholderValues(initialValues);
          })
          .catch(() => {
            setNhSubjectTemplate("");
            setNhBodyTemplate("");
            setNhTemplateName("");
            setTemplatePlaceholders({});
            form.setValue("subject", "");
            form.setValue("body", "");
          })
          .finally(() => {
            setNhLoading(false);
          });
      } else {
        setNhLoading(false);
        setNhSubjectTemplate("");
        setNhBodyTemplate("");
        setNhTemplateName("");
        setTemplatePlaceholders({});
        // Anche senza template NH, pre-popola i valori dal context per i token legacy
        setPlaceholderValues({ ...contextPlaceholderValues });
      }


    } else {
      setNhTemplateName("");
      setTemplatePlaceholders({});
      setPlaceholderTouched({});
      setPlaceholderValidationAttempted(false);
      onResetForm();
    }
  };

  useEffect(() => {
    const tid = form.getValues("templateId");
    onChangeTemplate(tid);
  }, [form.watch("templateId")])

  // Quando i dati locali arrivano (caricamento async), aggiorna i placeholder
  // se c'è già un template selezionato
  useEffect(() => {
    const tid = form.getValues("templateId");
    if (!tid) return;
    setPlaceholderValues((prev) => {
      // Merge: i valori del context sovrascrivono solo quelli ancora vuoti o N/A
      // (non sovrascrivono modifiche manuali dell'operatore né valori reali già presenti)
      const updated = { ...prev };
      Object.entries(contextPlaceholderValues).forEach(([key, value]) => {
        const current = updated[key];
        const currentIsEmpty = current === undefined || current === "" || current?.startsWith("N/A_");
        const newIsReal = value !== "" && !value.startsWith("N/A_") && value !== "-";
        if (currentIsEmpty || newIsReal) {
          updated[key] = value;
        }
      });
      return updated;
    });
  }, [contextPlaceholderValues]);

  const possibleAttachmentsOptions = useMemo<AutoCompleteOption[]>(() => {
    return possibleAttachments.map((attachment) => ({
      label: attachment.name + ' - ' + attachment.label,
      value: attachment.customId,
    }));
  }, [possibleAttachments]);

  return (
    <Mui.Dialog open={props.open} onClose={onClose} fullWidth maxWidth="lg">
      <Mui.DialogTitle sx={{ pb: 1.5 }}>
        <Mui.Grid container alignItems={"center"}>
          <Mui.Grid item>
            <Mui.Typography variant="h5">
              {t("damageForm.details.messages.dialogs.newMessage.title")}
            </Mui.Typography>
          </Mui.Grid>
          <Mui.Grid item marginLeft={"auto"}>
            <Mui.Stack direction="row" spacing={1.5} alignItems="center">
              <Mui.Typography variant="body2" color="text.secondary">
                {t("damageForm.details.messages.dialogs.newMessage.labels.language")}
              </Mui.Typography>
              <FormInput
                name={nameof<DamageFormTypes.IMessageForm>("language")}
                type="select"
                options={languages}
                formInstance={form}
                variant="outlined"
                sx={{ minWidth: 110, width: 110 }}
              />
              <Mui.IconButton onClick={onClose}>
                <CloseIcon />
              </Mui.IconButton>
            </Mui.Stack>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.DialogTitle>

      <form onSubmit={form.handleSubmit((data) => sendMail(data))}>
        <Mui.DialogContent sx={{ pt: 2 }}>
          <Mui.Stack spacing={1.25}>
            <Mui.Grid container spacing={2}>
              <Mui.Grid item md={6} xs={12}>
                <FieldLabel>
                  {t("damageForm.details.messages.dialogs.newMessage.labels.template")}
                </FieldLabel>
                <FormInput
                  name={nameof<DamageFormTypes.IMessageForm>("templateId")}
                  type="select"
                  options={mailTemplateOptions}
                  formInstance={form}
                  variant="outlined"
                  fullWidth
                />
              </Mui.Grid>

              <Mui.Grid item md={6} xs={12}>
                <FieldLabel>
                  {t("damageForm.details.messages.dialogs.newMessage.labels.attachment")}
                </FieldLabel>
                <FormInput
                  name={nameof<DamageFormTypes.IMessageForm>(
                    "possibleAttachments"
                  )}
                  type="select"
                  options={possibleAttachmentsOptions}
                  value={form.watch("possibleAttachments")}
                  onChange={onChangeAttachments}
                  variant="outlined"
                  multiple={true}
                  limitTags={2}
                  fullWidth
                />
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Box>
              <Mui.Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
                <FieldLabel>
                  {t("damageForm.details.messages.dialogs.newMessage.labels.to")}
                </FieldLabel>
                {canEditTemplate && selectedTemplateCode && selectedTemplateCode !== "CUSTOM" && (
                  <Mui.Button
                    size="small"
                    onClick={openRecipientsDialog}
                  >
                    {t("damageForm.details.messages.dialogs.newMessage.labels.editRecipients")}
                  </Mui.Button>
                )}
              </Mui.Stack>
              <FormInput
                name={nameof<DamageFormTypes.IMessageForm>("to")}
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                formInstance={form}
                onChange={(value) => {
                  form.setValue("to", value, {
                    shouldValidate: Boolean(form.formState.errors.to),
                    shouldDirty: true,
                  });
                }}
                validation={{
                  required: t("damageForm.details.messages.dialogs.newMessage.toErrorMessage"),
                  validate: (value: string) => {
                    const cleaned = (value || "").replace(/\s/g, "").replace(/,$/, "");
                    // Il required gestisce il campo vuoto
                    if (!cleaned) return true;

                    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
                    const parts = cleaned.split(",");

                    // Più indirizzi presenti ma non separati da virgola:
                    // un singolo segmento contiene più di un carattere '@'.
                    const hasMultipleUnseparated = parts.some(
                      (part) => (part.match(/@/g) || []).length > 1
                    );
                    if (hasMultipleUnseparated) {
                      return t("damageForm.details.messages.dialogs.newMessage.toEmailErrorMessage");
                    }

                    // Ogni indirizzo (singolo o separato da virgola) deve avere un formato valido.
                    const allValid = parts.every((part) => emailRegex.test(part));
                    return (
                      allValid ||
                      t("damageForm.details.messages.dialogs.newMessage.toEmailFormatErrorMessage")
                    );
                  }
                }}
              />
            </Mui.Box>

            <Mui.Box>
              <FieldLabel>
                {t("damageForm.details.messages.dialogs.newMessage.labels.subject")}
              </FieldLabel>
              {nhLoading ? (
                <Mui.Skeleton variant="rectangular" height={40} />
              ) : (
                <Mui.TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  disabled={!isFreeEditTemplate}
                  value={isFreeEditTemplate ? form.watch("subject") : previewSubject}
                  onChange={(e) => {
                    if (isFreeEditTemplate) form.setValue("subject", e.target.value);
                  }}
                  InputProps={{ readOnly: !isFreeEditTemplate, sx: { backgroundColor: isFreeEditTemplate ? undefined : "#f5f5f5" } }}
                />
              )}
            </Mui.Box>

            <Mui.Box>
              <FieldLabel>
                {t("damageForm.details.messages.dialogs.newMessage.labels.message")}
              </FieldLabel>
              {nhLoading ? (
                <Mui.Skeleton variant="rectangular" height={120} />
              ) : (
                <Mui.TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={6}
                  disabled={!isFreeEditTemplate}
                  value={isFreeEditTemplate ? form.watch("body") : previewBody}
                  onChange={(e) => {
                    if (isFreeEditTemplate) form.setValue("body", e.target.value);
                  }}
                  InputProps={{ readOnly: !isFreeEditTemplate, sx: { backgroundColor: isFreeEditTemplate ? undefined : "#f5f5f5" } }}
                />
              )}
            </Mui.Box>

            {editablePlaceholders.length > 0 && (
              <Mui.Box>
                <Mui.Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Placeholder
                </Mui.Typography>
                <Mui.Grid container spacing={2}>
                  {editablePlaceholders.map((ph) => {
                    const meta = PLACEHOLDER_META[ph.templateParam] || { label: ph.templateParam, required: false, editable: true };
                    const placeholderLabel = getPlaceholderLabel(ph.templateParam);
                    const placeholderError = placeholderErrors[ph.templateParam];
                    return (
                      <Mui.Grid item md={6} xs={12} key={ph.templateParam}>
                        <FieldLabel>{placeholderLabel + (meta.required ? " *" : "")}</FieldLabel>
                        <Mui.TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled={!meta.editable}
                          value={placeholderValues[ph.templateParam] || ""}
                          error={Boolean(placeholderError)}
                          helperText={placeholderError || undefined}
                          inputProps={{
                            inputMode: AMOUNT_PLACEHOLDERS.has(ph.templateParam) ? "decimal" : undefined,
                          }}
                          onChange={(e) => {
                            setPlaceholderTouched((prev) => ({
                              ...prev,
                              [ph.templateParam]: true,
                            }));
                            setPlaceholderValues((prev) => ({
                              ...prev,
                              [ph.templateParam]: e.target.value,
                            }));
                          }}
                          onBlur={() => {
                            setPlaceholderTouched((prev) => ({
                              ...prev,
                              [ph.templateParam]: true,
                            }));
                          }}
                        />
                      </Mui.Grid>
                    );
                  })}
                </Mui.Grid>
              </Mui.Box>
            )}
          </Mui.Stack>
        </Mui.DialogContent>

        <Mui.DialogActions sx={{ px: 3, py: 2 }}>
          {canViewTemplate && nhTemplateName && (
            <Mui.Button
              size="smallPlus"
              endIcon={<OpenInNewRoundedIcon />}
              sx={{ mr: "auto" }}
              onClick={() =>
                window.open(`/notification-hub/view/${nhTemplateName}`, "_blank")
              }
            >
              <Mui.Typography color={"inherit"} variant="h6">
                {t("damageForm.details.messages.dialogs.newMessage.labels.detailTemplate")}
              </Mui.Typography>
            </Mui.Button>
          )}
          <Mui.Button variant="outlined" onClick={onClose} size="smallPlus">
            <Mui.Typography color={"inherit"} variant="h6">
              {t("damageForm.details.messages.dialogs.newMessage.btnCancel")}
            </Mui.Typography>
          </Mui.Button>
          <Mui.Button type="submit" size="smallPlus">
            <Mui.Typography color={"inherit"} variant="h6">
              {t("damageForm.details.messages.dialogs.newMessage.btnSend")}
            </Mui.Typography>
          </Mui.Button>
        </Mui.DialogActions>
      </form>

      {/* LPP-24664 (P3): dialog di modifica dei destinatari di default del template. */}
      <Mui.Dialog open={recipientsDialogOpen} onClose={() => setRecipientsDialogOpen(false)} fullWidth maxWidth="sm">
        <Mui.DialogTitle>
          {t("damageForm.details.messages.dialogs.newMessage.recipientsDialog.title")}
        </Mui.DialogTitle>
        <Mui.DialogContent>
          <Mui.Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("damageForm.details.messages.dialogs.newMessage.recipientsDialog.hint")}
          </Mui.Typography>
          <Mui.TextField
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            value={recipientsDraft}
            onChange={(e) => setRecipientsDraft(e.target.value)}
            placeholder="mario@octo.com, luigi@octo.com"
          />
        </Mui.DialogContent>
        <Mui.DialogActions sx={{ px: 3, py: 2 }}>
          <Mui.Button variant="outlined" size="smallPlus" onClick={() => setRecipientsDialogOpen(false)} disabled={savingRecipients}>
            {t("damageForm.details.messages.dialogs.newMessage.btnCancel")}
          </Mui.Button>
          <Mui.Button size="smallPlus" onClick={saveRecipients} disabled={savingRecipients}>
            {t("damageForm.details.messages.dialogs.newMessage.recipientsDialog.save")}
          </Mui.Button>
        </Mui.DialogActions>
      </Mui.Dialog>
    </Mui.Dialog>
  );
};
export default NewMessages;
